import { cleanDescription } from './gameFormat';

const normalizeLanguage = (language) =>
  String(language || 'en').split('-')[0].toLowerCase();

const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ');

const normalizeTitle = (value) =>
  normalizeLabel(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const labelTranslations = {
  fr: {
    'abstract strategy': 'Stratégie abstraite',
    acting: 'Mime et interprétation',
    'action drafting': "Sélection d'actions",
    'action points': "Points d'action",
    adventure: 'Aventure',
    animals: 'Animaux',
    'area majority / influence': 'Majorité / influence de zone',
    'area movement': 'Déplacement de zone',
    auction: 'Enchères',
    bluffing: 'Bluff',
    'card game': 'Jeu de cartes',
    citybuilding: 'Construction de ville',
    civilization: 'Civilisation',
    'closed drafting': 'Draft fermé',
    'cooperative game': 'Jeu coopératif',
    deduction: 'Déduction',
    dice: 'Dés',
    'dice rolling': 'Lancer de dés',
    economic: 'Économie',
    educational: 'Éducatif',
    exploration: 'Exploration',
    fantasy: 'Fantasy',
    fighting: 'Combat',
    'hand management': 'Gestion de main',
    memory: 'Mémoire',
    miniatures: 'Figurines',
    negotiation: 'Négociation',
    'open drafting': 'Draft ouvert',
    'pattern building': 'Construction de motifs',
    political: 'Politique',
    'push your luck': 'Stop ou encore',
    racing: 'Course',
    'role playing': 'Jeu de rôle',
    'route/network building': 'Construction de routes / réseaux',
    'score-and-reset game': 'Score et réinitialisation',
    'set collection': 'Collection de séries',
    simulation: 'Simulation',
    'solo / solitaire game': 'Jeu solo',
    'tile placement': 'Placement de tuiles',
    travel: 'Voyage',
    'trick-taking': 'Jeu de plis',
    'variable player powers': 'Pouvoirs asymétriques',
    wargame: 'Jeu de guerre',
    'worker placement': "Placement d'ouvriers",
  },
};

const frenchDescriptions = {
  'ark-nova':
    'Dans Ark Nova, vous développez un zoo moderne et scientifiquement reconnu. Construisez des enclos, soutenez des projets de conservation et équilibrez attrait du public et impact écologique.',
  'brass-birmingham':
    "Brass: Birmingham vous plonge dans la révolution industrielle anglaise. Développez des réseaux, exploitez des industries et adaptez-vous aux marchés pour bâtir l'empire économique le plus solide.",
  cascadia:
    "Cascadia est un jeu de placement de tuiles et de jetons où vous composez un habitat du Nord-Ouest Pacifique. Assemblez les paysages et créez les meilleurs groupes d'animaux pour marquer des points.",
  'dune-imperium':
    "Dune: Imperium mêle deckbuilding et placement d'ouvriers dans l'univers de Dune. Envoyez vos agents, gagnez l'influence des factions et contrôlez l'Épice pour dominer Arrakis.",
  everdell:
    "Dans Everdell, vous développez une cité forestière pleine de créatures et de constructions. Placez vos ouvriers, jouez vos cartes au bon moment et préparez la plus belle ville avant l'hiver.",
  faraway:
    'Faraway est un jeu de cartes tactique où chaque région jouée prépare le voyage retour. Planifiez vos symboles, choisissez le bon tempo et révélez votre parcours dans le bon ordre.',
  heat:
    'Heat vous installe au volant de bolides des années 1960. Gérez la surchauffe de votre moteur, prenez les bons virages et choisissez le moment idéal pour dépasser vos adversaires.',
  mow:
    'MOW est un jeu de cartes rapide et malin où les joueurs rassemblent des troupeaux tout en évitant les mouches. Posez vos vaches au bon moment et laissez les ennuis aux autres.',
  root:
    "Root est un jeu asymétrique de conquête et d'influence dans une forêt en guerre. Chaque faction joue selon ses propres règles, entre expansion militaire, rébellion et opportunisme.",
  'schotten-totten':
    "Dans Schotten Totten, deux clans s'affrontent pour contrôler des bornes frontalières. Formez les meilleures combinaisons de cartes et gagnez assez de positions pour imposer votre clan.",
  'skip-bo':
    'Skip-Bo est un jeu de cartes familial où il faut vider sa pile personnelle en construisant des suites communes. Gardez le rythme, bloquez au bon moment et soyez le premier à tout poser.',
  skyjo:
    'Dans Skyjo, remplacez astucieusement vos cartes pour obtenir le score le plus bas possible. Observez, prenez des risques mesurés et déclenchez la fin de manche au bon moment.',
  'terraforming-mars':
    "Terraforming Mars vous met à la tête d'une corporation chargée de rendre Mars habitable. Produisez des ressources, développez des projets et augmentez température, oxygène et océans.",
  wingspan:
    'Wingspan est un jeu de collection et de moteur autour des oiseaux. Attirez des espèces dans vos habitats, activez leurs pouvoirs et construisez une réserve naturelle efficace et élégante.',
};

const pickLocalizedValue = (value, language, fallback) => {
  const locale = normalizeLanguage(language);

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value[locale] || value.en || fallback;
  }

  return fallback;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export const translateBggLabel = (label, language = 'en') => {
  const locale = normalizeLanguage(language);
  const dictionary = labelTranslations[locale] || {};

  return dictionary[normalizeLabel(label)] || label;
};

export const localizedGameTitle = (game, language = 'en', t) =>
  pickLocalizedValue(
    game?.titleLocales,
    language,
    game?.title || game?.name || t?.('games.meta.untitled', 'Untitled game') || 'Untitled game'
  );

export const localizedPrimaryTags = (game, language = 'en', limit = 3) => {
  const mechanics = asArray(pickLocalizedValue(game?.mechanicLocales, language, game?.mechanics));
  const categories = asArray(pickLocalizedValue(game?.categoryLocales, language, game?.categories));
  const tags = [...mechanics, ...categories];

  return tags.slice(0, limit).map((tag) => translateBggLabel(tag, language));
};

export const localizedDescription = (game, language = 'en', t) => {
  const locale = normalizeLanguage(language);
  const fromLocales = pickLocalizedValue(game?.descriptionLocales, language, '');

  if (fromLocales) {
    return { text: cleanDescription(fromLocales), isFallback: false };
  }

  const titleKey = normalizeTitle(game?.title || game?.name);
  const translated = locale === 'fr' ? frenchDescriptions[titleKey] : null;

  if (translated) {
    return { text: translated, isFallback: false };
  }

  const englishDescription = cleanDescription(game?.description);

  if (englishDescription) {
    return {
      text: englishDescription,
      isFallback: locale !== 'en',
    };
  }

  return {
    text:
      t?.('games.meta.noDescription') ||
      'No description has been written for this game yet.',
    isFallback: false,
  };
};

export const localizedList = (values = [], language = 'en') =>
  asArray(values).map((value) => translateBggLabel(value, language)).join(', ');
